import { useDemoStore } from '@/stores/demoStore';
import {
  ASSET_LICENSES,
  DEMO_TERMS,
  SOFTWARE_LICENSES,
  type DemoCatalogRelease,
  type DemoProject,
  type DemoQuiz,
  type DemoQuizVersion,
  type DemoRelease,
  type DemoState,
  type DemoTeam,
} from '@/demo/demoData';
import type {
  LabCredit,
  LabDiscoverItem,
  LabProjectDetail,
  LabProjectSummary,
  LabQuizQuestion,
  LabQuizView,
  LabReleaseStatus,
  LabShowcaseState,
  LabTeamDetail,
  LabTeamRole,
  LabVisibility,
} from '@/lib/types';

const BLOCKED_MESSAGE = 'Uploads and submissions are not available in the demo.';
const SOURCE_MESSAGE = 'Source download is not available in the demo.';
const OPEN_STATUSES = new Set<LabReleaseStatus>(['draft', 'in_review', 'changes_requested']);
const PUBLISHED_HISTORY = new Set<LabReleaseStatus>(['published', 'superseded', 'withdrawn']);
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const RANK: Record<LabVisibility, number> = { private: 0, unit: 1, organization: 2 };

const BLOCKED = new Set([
  '/labs/builds/initiate',
  '/labs/builds/part-urls',
  '/labs/builds/complete',
  '/labs/builds/abort',
  '/labs/builds/select-executable',
  '/labs/builds/attach',
  '/labs/builds/remove',
  '/labs/sources/initiate',
  '/labs/sources/part-urls',
  '/labs/sources/complete',
  '/labs/sources/abort',
  '/labs/sources/attach',
  '/labs/sources/remove',
  '/labs/releases/media',
  '/labs/releases/submit',
  '/labs/releases/cancel-submission',
  '/labs/review/decide',
  '/labs/showcase/submit',
]);

type Body = Record<string, unknown>;
type DemoResult = { ok: true; data: unknown } | { ok: false; status: number; message: string };
type Handler = (body: Body) => DemoResult;

const ok = (data: unknown): DemoResult => ({ ok: true, data });
const fail = (status: number, message: string): DemoResult => ({ ok: false, status, message });
const nowIso = () => new Date().toISOString();
const text = (value: unknown) => (typeof value === 'string' ? value : '');
const state = () => useDemoStore.getState().data;
const mutate = (fn: (draft: DemoState) => void) => useDemoStore.getState().updateData(fn);

function asBody(value: unknown): Body {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Body;
  return {};
}

function normalize(url: string): string {
  const path = url.split('?')[0] ?? url;
  const marker = '/api/v1';
  const index = path.indexOf(marker);
  return index >= 0 ? path.slice(index + marker.length) : path;
}

function pageNumber(value: unknown, fallback = 1): number {
  const page = Number(value);
  if (!Number.isFinite(page) || page < 1) return fallback;
  return Math.floor(page);
}

function roleOf(draft: DemoState, teamId: string | undefined): LabTeamRole | null {
  if (!teamId) return null;
  const team = draft.teams.find((item) => item.id === teamId);
  return team?.members.find((member) => member.userId === draft.user.id)?.role ?? null;
}

function teamOf(draft: DemoState, teamId: string): DemoTeam | null {
  return draft.teams.find((team) => team.id === teamId) ?? null;
}

function isActive(draft: DemoState, userId: string): boolean {
  const person = draft.people.find((item) => item.id === userId);
  return Boolean(person) && person?.inOrganization !== false;
}

function hasActiveOwner(draft: DemoState, team: DemoTeam): boolean {
  return team.members.some((member) => member.role === 'owner' && isActive(draft, member.userId));
}

function hasActiveManager(draft: DemoState, team: DemoTeam): boolean {
  return team.members.some((member) => member.role === 'manager' && isActive(draft, member.userId));
}

function unitOf(draft: DemoState, unitId: string | null) {
  if (!unitId) return null;
  const unit = draft.units.find((item) => item.id === unitId);
  if (!unit) return null;
  return { id: unit.id, code: unit.code, name: unit.name };
}

function nameTaken(draft: DemoState, name: string, exceptId?: string): boolean {
  const key = name.trim().toLowerCase();
  return draft.teams.some((team) => team.id !== exceptId && team.name.trim().toLowerCase() === key);
}

function displayRelease(releases: DemoRelease[]): DemoRelease | null {
  const visible = releases.filter((release) => release.status !== 'superseded');
  const published = visible.find((release) => release.status === 'published');
  const open = visible.find((release) => OPEN_STATUSES.has(release.status));
  const latest = [...visible].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  return published ?? open ?? latest ?? null;
}

function publishedRelease(project: DemoProject): DemoRelease | null {
  return project.releases.find((release) => release.status === 'published') ?? null;
}

function openRelease(project: DemoProject): DemoRelease | null {
  return project.releases.find((release) => OPEN_STATUSES.has(release.status)) ?? null;
}

function everPublished(project: DemoProject): boolean {
  return project.releases.some((release) => PUBLISHED_HISTORY.has(release.status));
}

function findProject(draft: DemoState, projectId: string): DemoProject | null {
  const project = draft.projects.find((item) => item.id === projectId);
  if (!project || !roleOf(draft, project.teamId)) return null;
  return project;
}

function findRelease(draft: DemoState, releaseId: string): { project: DemoProject; release: DemoRelease } | null {
  for (const project of draft.projects) {
    const release = project.releases.find((item) => item.id === releaseId);
    if (release && roleOf(draft, project.teamId)) return { project, release };
  }
  return null;
}

function catalogRelease(draft: DemoState, releaseId: string): DemoCatalogRelease | null {
  return draft.catalog.find((item) => item.releaseId === releaseId) ?? null;
}

function parentOf(draft: DemoState, releaseId: string | null) {
  if (!releaseId) return null;
  const owned = draft.projects.flatMap((project) => project.releases).find((release) => release.id === releaseId);
  if (owned) return { title: owned.title, version: owned.version, withdrawn: owned.status === 'withdrawn' };
  const catalog = catalogRelease(draft, releaseId);
  if (!catalog) return null;
  return { title: catalog.title, version: catalog.version, withdrawn: catalog.withdrawn };
}

function remixableRelease(release: DemoRelease): boolean {
  const source = release.sourcePackage;
  const structureReady = !source || source.engine !== 'unknown' || Boolean(source.structureConfirmedAt);
  return release.remixPermission === 'organization'
    && Boolean(release.assetSharingConfirmedAt)
    && source?.status === 'passed'
    && structureReady;
}

function toDetail(draft: DemoState, project: DemoProject): LabProjectDetail {
  const role = roleOf(draft, project.teamId) ?? 'member';
  const team = teamOf(draft, project.teamId);
  const open = openRelease(project);
  const published = publishedRelease(project);
  const passedBuilds = project.releases.flatMap((release) => release.builds.filter((build) => build.status === 'passed'));
  const passedSources = project.releases.flatMap((release) => (
    release.sourcePackage?.status === 'passed' ? [release.sourcePackage] : []
  ));
  return {
    project: {
      id: project.id,
      title: displayRelease(project.releases)?.title ?? 'Untitled project',
      unit: unitOf(draft, project.unitId),
      team: { id: project.teamId, name: team?.name ?? 'Team' },
      appId: project.appId,
      isDrmFree: project.isDrmFree,
      createdAt: project.createdAt,
    },
    role,
    permissions: {
      canManageMembers: role === 'owner' || role === 'manager',
      canPublishDirectly: false,
      canDelete: role === 'owner' && !everPublished(project),
      canChangeUnit: role === 'owner' && !published && open?.status !== 'in_review',
      canChangeDrm: !everPublished(project),
      canStartRelease: !open,
    },
    members: (team?.members ?? []).flatMap((member) => {
      const person = draft.people.find((item) => item.id === member.userId);
      if (!person) return [];
      return [{ userId: person.id, name: person.name, email: person.email, role: member.role }];
    }),
    openRelease: open,
    publishedRelease: published,
    history: [...project.releases]
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map((release) => ({
        id: release.id,
        version: release.version,
        status: release.status,
        submittedAt: release.submittedAt,
        publishedAt: release.publishedAt,
        reviewNote: release.reviewNote,
      })),
    reusableBuilds: passedBuilds,
    reusableSourcePackages: passedSources,
  };
}

function toSummary(draft: DemoState, project: DemoProject): LabProjectSummary {
  const visible = project.releases.filter((release) => release.status !== 'superseded');
  const display = displayRelease(visible);
  const open = visible.find((release) => OPEN_STATUSES.has(release.status));
  const published = visible.find((release) => release.status === 'published');
  const team = teamOf(draft, project.teamId);
  return {
    id: project.id,
    role: roleOf(draft, project.teamId) ?? 'member',
    team: { id: project.teamId, name: team?.name ?? 'Team' },
    title: display?.title ?? 'Untitled project',
    iconUrl: display?.iconUrl ?? null,
    bannerUrl: display?.bannerUrl ?? null,
    unit: unitOf(draft, project.unitId),
    publishedVersion: published?.version ?? null,
    openStatus: open?.status ?? null,
    parent: parentOf(draft, project.parentReleaseId),
    isTakenDown: !published && visible.some((release) => release.status === 'withdrawn'),
    updatedAt: project.updatedAt,
  };
}

function teamPermissions(draft: DemoState, team: DemoTeam) {
  const role = roleOf(draft, team.id) ?? 'member';
  return {
    canEditTeam: role === 'owner' || role === 'manager',
    canManageMembers: role === 'owner' || role === 'manager',
    canManageRoles: role === 'owner',
    canClaimOwnership: !hasActiveOwner(draft, team) && (role === 'manager' || !hasActiveManager(draft, team)),
  };
}

function toTeam(draft: DemoState, team: DemoTeam): LabTeamDetail {
  const projects = draft.projects
    .filter((project) => project.teamId === team.id)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return {
    team: { id: team.id, name: team.name, description: team.description, createdAt: team.createdAt },
    role: roleOf(draft, team.id) ?? 'member',
    hasActiveOwner: hasActiveOwner(draft, team),
    permissions: teamPermissions(draft, team),
    members: team.members.flatMap((member) => {
      const person = draft.people.find((item) => item.id === member.userId);
      if (!person) return [];
      return [{
        userId: person.id,
        name: person.name,
        email: person.email,
        role: member.role,
        inOrganization: person.inOrganization !== false,
      }];
    }),
    projects: projects.slice(0, 100).map((project) => ({
      id: project.id,
      title: displayRelease(project.releases)?.title ?? 'Untitled project',
      updatedAt: project.updatedAt,
    })),
    projectTotal: projects.length,
  };
}

function blankRelease(title: string, visibility: LabVisibility, at: string): DemoRelease {
  return {
    id: crypto.randomUUID(),
    version: '1.0.0',
    status: 'draft',
    visibility,
    title,
    description: null,
    credits: [],
    contentNotes: null,
    iconUrl: null,
    bannerUrl: null,
    submittedAt: null,
    reviewedAt: null,
    reviewNote: null,
    publishedAt: null,
    remixPermission: 'none',
    internalRemixTermsVersion: null,
    assetSharingConfirmedAt: null,
    createdAt: at,
    updatedAt: at,
    builds: [],
    sourcePackage: null,
  };
}

function nextVersion(version: string): string {
  const match = version.match(/^(.*?)(\d+)$/);
  if (!match) return `${version}.1`;
  return `${match[1]}${Number(match[2]) + 1}`;
}

function discoverFromRelease(draft: DemoState, project: DemoProject, release: DemoRelease): LabDiscoverItem {
  const unit = unitOf(draft, project.unitId);
  return {
    projectId: project.id,
    releaseId: release.id,
    appId: project.appId ?? 0,
    title: release.title,
    description: release.description,
    iconUrl: release.iconUrl,
    bannerUrl: release.bannerUrl,
    credits: release.credits,
    contentNotes: release.contentNotes,
    version: release.version,
    visibility: release.visibility,
    publishedAt: release.publishedAt,
    unit: unit ? { id: unit.id, code: unit.code, name: unit.name } : null,
    platforms: release.builds.map((build) => build.platform),
    remixable: remixableRelease(release),
    parent: parentOf(draft, project.parentReleaseId),
  };
}

function discoverFromCatalog(draft: DemoState, item: DemoCatalogRelease): LabDiscoverItem {
  return {
    projectId: item.projectId,
    releaseId: item.releaseId,
    appId: item.appId,
    title: item.title,
    description: item.description,
    iconUrl: null,
    bannerUrl: null,
    credits: item.credits,
    contentNotes: item.contentNotes,
    version: item.version,
    visibility: item.visibility,
    publishedAt: item.publishedAt,
    unit: item.unit ? { id: item.unit.id, code: item.unit.code, name: item.unit.name } : null,
    platforms: item.platforms,
    remixable: item.remixable,
    parent: parentOf(draft, item.parentReleaseId),
  };
}

function offerFor(draft: DemoState, releaseId: string) {
  const owned = findRelease(draft, releaseId);
  if (owned && owned.release.status === 'published') {
    const release = owned.release;
    return {
      releaseId: release.id,
      projectId: owned.project.id,
      title: release.title,
      version: release.version,
      organization: draft.organization.name,
      credits: release.credits,
      ancestors: ancestors(draft, owned.project.parentReleaseId),
      assetDeclaration: null,
      terms: { version: DEMO_TERMS.version, text: DEMO_TERMS.text, kind: 'internal' as const },
      eligible: remixableRelease(release),
      reason: remixableRelease(release) ? null : 'This version is not open for remixing.',
      parentReleaseId: release.id,
    };
  }
  const catalog = catalogRelease(draft, releaseId);
  if (!catalog || catalog.withdrawn) return null;
  return {
    releaseId: catalog.releaseId,
    projectId: catalog.projectId,
    title: catalog.title,
    version: catalog.version,
    organization: draft.organization.name,
    credits: catalog.credits,
    ancestors: ancestors(draft, catalog.parentReleaseId),
    assetDeclaration: null,
    terms: { version: DEMO_TERMS.version, text: DEMO_TERMS.text, kind: 'internal' as const },
    eligible: catalog.remixable,
    reason: catalog.remixable ? null : 'This version is not open for remixing.',
    parentReleaseId: catalog.releaseId,
  };
}

function ancestors(draft: DemoState, parentReleaseId: string | null) {
  const chain = [];
  let cursor = parentReleaseId;
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const owned = draft.projects.flatMap((project) => project.releases.map((release) => ({ project, release })))
      .find((item) => item.release.id === cursor);
    if (owned) {
      chain.push({
        releaseId: owned.release.id,
        title: owned.release.title,
        version: owned.release.version,
        organization: draft.organization.name,
        withdrawn: owned.release.status === 'withdrawn',
      });
      cursor = owned.project.parentReleaseId;
      continue;
    }
    const catalog = catalogRelease(draft, cursor);
    if (!catalog) break;
    chain.push({
      releaseId: catalog.releaseId,
      title: catalog.title,
      version: catalog.version,
      organization: draft.organization.name,
      withdrawn: catalog.withdrawn,
    });
    cursor = catalog.parentReleaseId;
  }
  return chain;
}

function quizView(quiz: DemoQuiz): LabQuizView {
  const versions = quiz.versions.map((version) => ({
    id: version.id,
    version: version.version,
    responseCount: version.responses.length,
    createdAt: version.createdAt,
  }));
  const current = quiz.versions.find((version) => version.id === quiz.currentVersionId) ?? null;
  return {
    id: quiz.id,
    projectId: quiz.projectId,
    title: quiz.title,
    status: quiz.status,
    updatedAt: quiz.updatedAt,
    draft: quiz.draft,
    totalResponses: quiz.versions.reduce((sum, version) => sum + version.responses.length, 0),
    currentVersion: current
      ? { id: current.id, version: current.version, responseCount: current.responses.length, createdAt: current.createdAt }
      : null,
    versions,
  };
}

function findQuiz(draft: DemoState, quizId: string): { project: DemoProject; quiz: DemoQuiz } | null {
  for (const project of draft.projects) {
    const quiz = project.quizzes.find((item) => item.id === quizId);
    if (quiz && roleOf(draft, project.teamId)) return { project, quiz };
  }
  return null;
}

function choiceCount(version: DemoQuizVersion, question: LabQuizQuestion, label: string): number {
  return version.responses.filter((response) => {
    const answer = response.answers.find((item) => item.questionId === question.id)?.answer ?? '';
    return answer.split(', ').includes(label);
  }).length;
}

function quizSummary(version: DemoQuizVersion) {
  return {
    version: version.version,
    responseCount: version.responses.length,
    complete: true,
    questions: version.questions.map((question) => ({
      id: question.id,
      kind: question.kind,
      prompt: question.prompt,
      options: question.options?.map((option) => ({
        id: option.id,
        label: option.label,
        count: choiceCount(version, question, option.label),
      })),
    })),
  };
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function fileSlug(title: string): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return slug || 'quiz';
}

function nameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email;
  const words = local.split(/[._-]+/).filter(Boolean);
  if (words.length === 0) return email;
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function sameOrg(slug: unknown): DemoResult | null {
  const current = state();
  if (!current || slug !== current.organization.slug) return fail(404, 'Labs is not available');
  return null;
}

function context(body: Body): DemoResult {
  const current = state();
  if (!current) return fail(404, 'Labs is not available');
  if (body.orgSlug && body.orgSlug !== current.organization.slug) return fail(404, 'Labs is not available');
  const user = current.user;
  return ok({
    organization: current.organization,
    user: { id: user.id, name: `${user.firstName} ${user.lastName ?? ''}`.trim(), email: user.email, role: user.role },
    defaultVisibility: 'organization',
    remixingEnabled: true,
    requiresLabsUnit: user.role === 'student',
    units: current.units,
  });
}

function listProjects(body: Body): DemoResult {
  const denied = sameOrg(body.orgSlug);
  if (denied) return denied;
  const current = state()!;
  const projects = current.projects
    .filter((project) => roleOf(current, project.teamId))
    .map((project) => toSummary(current, project))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return ok(projects);
}

function createProject(body: Body): DemoResult {
  const denied = sameOrg(body.orgSlug);
  if (denied) return denied;
  const title = text(body.title).trim();
  if (!title) return fail(400, 'Add a title');
  const unitId = typeof body.unitId === 'string' ? body.unitId : null;
  const current = state();
  if (!current) return fail(404, 'Labs is not available');
  if (unitId && !current.units.some((unit) => unit.id === unitId)) {
    return fail(403, 'You can only link a project to a unit you are part of');
  }
  const teamId = text(body.teamId);
  const teamName = text(body.teamName).trim();
  if (Boolean(teamId) === Boolean(teamName)) return fail(400, 'Choose a team or name a new one');
  if (teamId && !roleOf(current, teamId)) return fail(404, 'Team not found');
  if (teamName && nameTaken(current, teamName)) return fail(409, 'A team with this name already exists');

  let detail: LabProjectDetail | null = null;
  mutate((draft) => {
    const at = nowIso();
    const team = teamId
      ? draft.teams.find((item) => item.id === teamId)!
      : {
        id: crypto.randomUUID(),
        name: teamName,
        description: null,
        createdAt: at,
        updatedAt: at,
        members: [{ userId: draft.user.id, role: 'owner' as const }],
      };
    if (!teamId) draft.teams.unshift(team);
    const project: DemoProject = {
      id: crypto.randomUUID(),
      teamId: team.id,
      unitId,
      appId: null,
      isDrmFree: false,
      createdAt: at,
      updatedAt: at,
      parentReleaseId: null,
      releases: [blankRelease(title, 'organization', at)],
      quizzes: [],
      showcase: { submission: null },
    };
    draft.projects.unshift(project);
    detail = toDetail(draft, project);
  });
  return ok(detail);
}

function getProject(body: Body): DemoResult {
  const current = state();
  const project = current ? findProject(current, text(body.projectId)) : null;
  if (!current || !project) return fail(404, 'Project not found');
  return ok(toDetail(current, project));
}

function updateUnit(body: Body): DemoResult {
  const current = state();
  const project = current ? findProject(current, text(body.projectId)) : null;
  if (!current || !project) return fail(404, 'Project not found');
  if (roleOf(current, project.teamId) !== 'owner') return fail(403, 'Only a team owner can change the unit');
  if (publishedRelease(project) || openRelease(project)?.status === 'in_review') {
    return fail(400, 'The unit is locked while a release is published or in review');
  }
  const unitId = typeof body.unitId === 'string' ? body.unitId : null;
  if (unitId && !current.units.some((unit) => unit.id === unitId)) {
    return fail(403, 'You can only link a project to a unit you are part of');
  }
  let detail: LabProjectDetail | null = null;
  mutate((draft) => {
    const next = findProject(draft, project.id);
    if (!next) return;
    next.unitId = unitId;
    next.updatedAt = nowIso();
    detail = toDetail(draft, next);
  });
  return ok(detail);
}

function updateDrm(body: Body): DemoResult {
  const current = state();
  const project = current ? findProject(current, text(body.projectId)) : null;
  if (!current || !project) return fail(404, 'Project not found');
  if (everPublished(project)) return fail(400, 'This choice is locked after the first published release');
  let detail: LabProjectDetail | null = null;
  mutate((draft) => {
    const next = findProject(draft, project.id);
    if (!next) return;
    next.isDrmFree = body.isDrmFree === true;
    next.updatedAt = nowIso();
    detail = toDetail(draft, next);
  });
  return ok(detail);
}

function deleteProject(body: Body): DemoResult {
  const current = state();
  const project = current ? findProject(current, text(body.projectId)) : null;
  if (!current || !project) return fail(404, 'Project not found');
  if (roleOf(current, project.teamId) !== 'owner' || everPublished(project)) {
    return fail(400, 'This project cannot be deleted');
  }
  mutate((draft) => {
    draft.projects = draft.projects.filter((item) => item.id !== project.id);
  });
  return ok({ success: true });
}

function listTeams(body: Body): DemoResult {
  const denied = sameOrg(body.orgSlug);
  if (denied) return denied;
  const current = state()!;
  const page = pageNumber(body.page);
  const pageSize = pageNumber(body.pageSize, 24);
  const teams = current.teams
    .filter((team) => roleOf(current, team.id))
    .map((team) => ({
      id: team.id,
      name: team.name,
      description: team.description,
      role: roleOf(current, team.id) ?? 'member' as const,
      memberCount: team.members.length,
      projectCount: current.projects.filter((project) => project.teamId === team.id).length,
      hasActiveOwner: hasActiveOwner(current, team),
      updatedAt: team.updatedAt,
    }))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const start = (page - 1) * pageSize;
  return ok({ teams: teams.slice(start, start + pageSize), page, pageSize, total: teams.length });
}

function createTeam(body: Body): DemoResult {
  const denied = sameOrg(body.orgSlug);
  if (denied) return denied;
  const name = text(body.name).trim();
  if (!name) return fail(400, 'Add a team name');
  const current = state();
  if (!current) return fail(404, 'Labs is not available');
  if (nameTaken(current, name)) return fail(409, 'A team with this name already exists');
  const description = text(body.description).trim() || null;
  let detail: LabTeamDetail | null = null;
  mutate((draft) => {
    const at = nowIso();
    const team: DemoTeam = {
      id: crypto.randomUUID(),
      name,
      description,
      createdAt: at,
      updatedAt: at,
      members: [{ userId: draft.user.id, role: 'owner' }],
    };
    draft.teams.unshift(team);
    detail = toTeam(draft, team);
  });
  return ok(detail);
}

function getTeam(body: Body): DemoResult {
  const current = state();
  const team = current ? teamOf(current, text(body.teamId)) : null;
  if (!current || !team || !roleOf(current, team.id)) return fail(404, 'Team not found');
  return ok(toTeam(current, team));
}

function updateTeam(body: Body): DemoResult {
  const current = state();
  const team = current ? teamOf(current, text(body.teamId)) : null;
  if (!current || !team || !roleOf(current, team.id)) return fail(404, 'Team not found');
  const role = roleOf(current, team.id);
  if (role === 'member') return fail(403, 'Only owners and managers can edit this team');
  const name = text(body.name).trim();
  if (!name) return fail(400, 'Add a team name');
  if (nameTaken(current, name, team.id)) return fail(409, 'A team with this name already exists');
  let detail: LabTeamDetail | null = null;
  mutate((draft) => {
    const next = teamOf(draft, team.id);
    if (!next) return;
    next.name = name;
    next.description = text(body.description).trim() || null;
    next.updatedAt = nowIso();
    detail = toTeam(draft, next);
  });
  return ok(detail);
}

function addMember(body: Body): DemoResult {
  const current = state();
  const team = current ? teamOf(current, text(body.teamId)) : null;
  if (!current || !team || !roleOf(current, team.id)) return fail(404, 'Team not found');
  const role = roleOf(current, team.id);
  if (role !== 'owner' && role !== 'manager') return fail(403, 'Only owners and managers can add members');
  const email = text(body.email).trim().toLowerCase();
  if (!EMAIL.test(email)) return fail(400, 'Enter an email address');
  const memberRole: LabTeamRole = body.role === 'manager' && role === 'owner' ? 'manager' : 'member';
  if (team.members.some((member) => current.people.find((person) => person.id === member.userId)?.email === email)) {
    return fail(409, 'This person is already on the team');
  }
  let detail: LabTeamDetail | null = null;
  mutate((draft) => {
    const next = teamOf(draft, team.id);
    if (!next) return;
    const existing = draft.people.find((person) => person.email === email);
    const person = existing ?? { id: crypto.randomUUID(), name: nameFromEmail(email), email };
    if (!existing) draft.people.push(person);
    next.members.push({ userId: person.id, role: memberRole });
    next.updatedAt = nowIso();
    detail = toTeam(draft, next);
  });
  return ok(detail);
}

function removeMember(body: Body): DemoResult {
  const current = state();
  const team = current ? teamOf(current, text(body.teamId)) : null;
  if (!current || !team || !roleOf(current, team.id)) return fail(404, 'Team not found');
  const userId = text(body.userId);
  const member = team.members.find((item) => item.userId === userId);
  if (!member) return fail(404, 'That member is not on this team');
  const activeOwners = team.members.filter((item) => item.role === 'owner' && isActive(current, item.userId));
  if (member.role === 'owner' && isActive(current, member.userId) && activeOwners.length <= 1) {
    return fail(400, 'A team needs an owner');
  }
  const actor = roleOf(current, team.id);
  const self = userId === current.user.id;
  const allowed = self || actor === 'owner' || (actor === 'manager' && member.role === 'member');
  if (!allowed) return fail(403, 'You cannot remove this member');
  mutate((draft) => {
    const next = teamOf(draft, team.id);
    if (!next) return;
    next.members = next.members.filter((item) => item.userId !== userId);
    next.updatedAt = nowIso();
  });
  if (self) return ok(null);
  const latest = state();
  const updated = latest ? teamOf(latest, team.id) : null;
  if (!latest || !updated) return fail(404, 'Team not found');
  return ok(toTeam(latest, updated));
}

function setRole(body: Body): DemoResult {
  const current = state();
  const team = current ? teamOf(current, text(body.teamId)) : null;
  if (!current || !team || roleOf(current, team.id) !== 'owner') return fail(403, 'Only a team owner can change roles');
  const userId = text(body.userId);
  const member = team.members.find((item) => item.userId === userId);
  if (!member) return fail(404, 'Member not found');
  const nextRole: LabTeamRole = body.role === 'owner' || body.role === 'manager' ? body.role : 'member';
  if (nextRole === 'owner' && !isActive(current, userId)) return fail(400, 'This person has left the university');
  const otherActiveOwner = team.members.some((item) => (
    item.role === 'owner' && item.userId !== current.user.id && isActive(current, item.userId)
  ));
  if (nextRole !== 'owner' && userId === current.user.id && !otherActiveOwner) {
    return fail(400, 'Make someone else an owner first');
  }
  let detail: LabTeamDetail | null = null;
  mutate((draft) => {
    const next = teamOf(draft, team.id);
    const target = next?.members.find((item) => item.userId === userId);
    if (!next || !target) return;
    target.role = nextRole;
    next.updatedAt = nowIso();
    detail = toTeam(draft, next);
  });
  return ok(detail);
}

function claimOwnership(body: Body): DemoResult {
  const current = state();
  const team = current ? teamOf(current, text(body.teamId)) : null;
  const role = current && team ? roleOf(current, team.id) : null;
  if (!current || !team || !role) return fail(404, 'Team not found');
  if (hasActiveOwner(current, team)) return fail(409, 'This team already has an owner');
  if (role !== 'manager' && hasActiveManager(current, team)) return fail(403, 'Only a manager can become owner');
  let detail: LabTeamDetail | null = null;
  mutate((draft) => {
    const next = teamOf(draft, team.id);
    const actor = next?.members.find((item) => item.userId === draft.user.id);
    if (!next || !actor) return;
    next.members = next.members.filter((item) => item.role !== 'owner' || item.userId === actor.userId);
    actor.role = 'owner';
    next.updatedAt = nowIso();
    detail = toTeam(draft, next);
  });
  return ok(detail);
}

function startRelease(body: Body): DemoResult {
  const current = state();
  const project = current ? findProject(current, text(body.projectId)) : null;
  if (!current || !project) return fail(404, 'Project not found');
  if (openRelease(project)) return fail(409, 'This project already has a release in progress');
  const base = publishedRelease(project) ?? project.releases[0];
  if (!base) return fail(404, 'Release not found');
  let detail: LabProjectDetail | null = null;
  mutate((draft) => {
    const next = findProject(draft, project.id);
    if (!next) return;
    const source = publishedRelease(next) ?? next.releases[0];
    if (!source) return;
    const used = new Set(next.releases.map((release) => release.version));
    let version = nextVersion(source.version);
    while (used.has(version)) version = nextVersion(version);
    const at = nowIso();
    const created: DemoRelease = {
      ...structuredClone(source),
      id: crypto.randomUUID(),
      version,
      status: 'draft',
      submittedAt: null,
      reviewedAt: null,
      reviewNote: null,
      publishedAt: null,
      createdAt: at,
      updatedAt: at,
    };
    next.releases.unshift(created);
    next.updatedAt = at;
    detail = toDetail(draft, next);
  });
  return ok(detail);
}

function updateRelease(body: Body): DemoResult {
  const current = state();
  const found = current ? findRelease(current, text(body.releaseId)) : null;
  if (!found) return fail(404, 'Release not found');
  if (found.release.status !== 'draft' && found.release.status !== 'changes_requested') {
    return fail(400, 'This version can no longer be edited');
  }
  const title = body.title === undefined ? found.release.title : text(body.title).trim();
  const version = body.version === undefined ? found.release.version : text(body.version).trim();
  if (!title || !version) return fail(400, 'Add a title and version');
  const duplicate = found.project.releases.some((release) => release.id !== found.release.id && release.version === version);
  if (duplicate) return fail(409, 'That version is already used');
  const visibility = body.visibility === 'private' || body.visibility === 'unit' || body.visibility === 'organization'
    ? body.visibility
    : found.release.visibility;
  if (visibility === 'unit' && !found.project.unitId) return fail(400, 'Link a unit before using that audience');
  let detail: LabProjectDetail | null = null;
  mutate((draft) => {
    const next = findRelease(draft, found.release.id);
    if (!next) return;
    const release = next.release;
    release.title = title;
    release.version = version;
    if ('description' in body) release.description = text(body.description).trim() || null;
    if ('contentNotes' in body) release.contentNotes = text(body.contentNotes).trim() || null;
    release.visibility = visibility;
    const credits = creditsFrom(body.credits);
    if (credits) {
      const permanent = release.credits.find((credit) => credit.permanent) ?? null;
      const editable = credits.filter((credit) => !credit.permanent);
      release.credits = permanent ? [permanent, ...editable] : editable;
    }
    if (typeof body.assetSharingConfirmed === 'boolean') {
      release.assetSharingConfirmedAt = body.assetSharingConfirmed ? (release.assetSharingConfirmedAt ?? nowIso()) : null;
      if (!body.assetSharingConfirmed) release.remixPermission = 'none';
    }
    if (body.remixPermission === 'organization' || body.remixPermission === 'none') {
      release.remixPermission = body.remixPermission;
      release.internalRemixTermsVersion = body.remixPermission === 'organization' ? DEMO_TERMS.version : null;
    }
    if (body.confirmSourceStructure === true && release.sourcePackage && !release.sourcePackage.structureConfirmedAt) {
      release.sourcePackage.structureConfirmedAt = nowIso();
    }
    const at = nowIso();
    release.updatedAt = at;
    next.project.updatedAt = at;
    detail = toDetail(draft, next.project);
  });
  return ok(detail);
}

function creditsFrom(value: unknown): LabCredit[] | null {
  if (!Array.isArray(value)) return null;
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const credit = item as LabCredit;
    const name = credit.name?.trim();
    const contribution = credit.contribution?.trim();
    if (!name || !contribution) return [];
    return [{ name, contribution, ...(credit.permanent ? { permanent: true } : {}) }];
  });
}

function narrowVisibility(body: Body): DemoResult {
  const current = state();
  const found = current ? findRelease(current, text(body.releaseId)) : null;
  if (!found) return fail(404, 'Release not found');
  if (found.release.status !== 'published') return fail(400, 'Only a published release can change audience here');
  const visibility = body.visibility;
  if (visibility !== 'private' && visibility !== 'unit' && visibility !== 'organization') {
    return fail(400, 'Choose who can play');
  }
  if (RANK[visibility] > RANK[found.release.visibility]) return fail(400, 'You can only narrow who can play');
  if (visibility === 'unit' && !found.project.unitId) return fail(400, 'Link a unit before using that audience');
  let detail: LabProjectDetail | null = null;
  mutate((draft) => {
    const next = findRelease(draft, found.release.id);
    if (!next) return;
    next.release.visibility = visibility;
    next.release.updatedAt = nowIso();
    next.project.updatedAt = next.release.updatedAt;
    detail = toDetail(draft, next.project);
  });
  return ok(detail);
}

function stopRemixing(body: Body): DemoResult {
  const current = state();
  const found = current ? findRelease(current, text(body.releaseId)) : null;
  if (!found) return fail(404, 'Release not found');
  if (found.release.status !== 'published') return fail(400, 'Only a published version can change remixing');
  let detail: LabProjectDetail | null = null;
  mutate((draft) => {
    const next = findRelease(draft, found.release.id);
    if (!next) return;
    next.release.remixPermission = 'none';
    next.release.internalRemixTermsVersion = null;
    next.release.updatedAt = nowIso();
    next.project.updatedAt = next.release.updatedAt;
    detail = toDetail(draft, next.project);
  });
  return ok(detail);
}

function discover(body: Body): DemoResult {
  const denied = sameOrg(body.orgSlug);
  if (denied) return denied;
  const current = state()!;
  const owned = current.projects.flatMap((project) => {
    const release = publishedRelease(project);
    if (!release || !roleOf(current, project.teamId)) return [];
    return [discoverFromRelease(current, project, release)];
  });
  const catalog = current.catalog.filter((item) => !item.withdrawn).map((item) => discoverFromCatalog(current, item));
  const items = [...owned, ...catalog].sort((a, b) => (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''));
  return ok(items);
}

function remixOffer(body: Body): DemoResult {
  const current = state();
  if (!current) return fail(404, 'Labs is not available');
  const offer = offerFor(current, text(body.releaseId));
  if (!offer) return fail(404, 'This version is not available to remix');
  return ok(offer);
}

function createRemix(body: Body): DemoResult {
  const denied = sameOrg(body.orgSlug);
  if (denied) return denied;
  const current = state();
  if (!current) return fail(404, 'Labs is not available');
  const offer = offerFor(current, text(body.parentReleaseId));
  if (!offer) return fail(404, 'This version is not available to remix');
  if (!offer.eligible) return fail(400, offer.reason ?? 'This version is not open for remixing.');
  if (text(body.termsVersion) !== DEMO_TERMS.version) return fail(400, 'The remix terms have changed. Read them again.');
  const title = text(body.title).trim();
  if (!title) return fail(400, 'Add a title');
  const unitId = typeof body.unitId === 'string' ? body.unitId : null;
  if (unitId && !current.units.some((unit) => unit.id === unitId)) {
    return fail(403, 'You can only link a project to a unit you are part of');
  }
  const teamId = text(body.teamId);
  const teamName = text(body.teamName).trim();
  if (Boolean(teamId) === Boolean(teamName)) return fail(400, 'Choose a team or name a new one');
  if (teamId && !roleOf(current, teamId)) return fail(404, 'Team not found');
  if (teamName && nameTaken(current, teamName)) return fail(409, 'A team with this name already exists');

  let detail: LabProjectDetail | null = null;
  mutate((draft) => {
    const at = nowIso();
    const team = teamId
      ? draft.teams.find((item) => item.id === teamId)!
      : {
        id: crypto.randomUUID(),
        name: teamName,
        description: null,
        createdAt: at,
        updatedAt: at,
        members: [{ userId: draft.user.id, role: 'owner' as const }],
      };
    if (!teamId) draft.teams.unshift(team);
    const user = draft.people.find((person) => person.id === draft.user.id);
    const release = blankRelease(title, 'organization', at);
    release.credits = [
      { name: offer.title.slice(0, 120), contribution: `Original ${offer.version}`.slice(0, 120), permanent: true },
      { name: user?.name ?? 'Demo Student', contribution: 'Remix' },
    ];
    const project: DemoProject = {
      id: crypto.randomUUID(),
      teamId: team.id,
      unitId,
      appId: null,
      isDrmFree: false,
      createdAt: at,
      updatedAt: at,
      parentReleaseId: offer.parentReleaseId,
      releases: [release],
      quizzes: [],
      showcase: { submission: null },
    };
    draft.projects.unshift(project);
    detail = toDetail(draft, project);
  });
  return ok({ project: detail, downloadUrl: null, expiresIn: 0 });
}

function lineage(body: Body): DemoResult {
  const current = state();
  const project = current ? findProject(current, text(body.projectId)) : null;
  if (!current || !project) return fail(404, 'Project not found');
  const page = pageNumber(body.page);
  const pageSize = pageNumber(body.pageSize, 20);
  const published = publishedRelease(project);
  const children = [
    ...current.projects.filter((item) => item.id !== project.id && item.parentReleaseId && published && item.parentReleaseId === published.id),
    ...current.catalog.filter((item) => published && item.parentReleaseId === published.id),
  ];
  const publishedChildren = children.flatMap((item) => {
    if ('releases' in item) {
      const release = publishedRelease(item);
      if (!release) return [];
      return [{
        projectId: item.id,
        title: release.title,
        version: release.version,
        status: release.status,
        parentVersion: published?.version ?? '',
        organization: current.organization.name,
        withdrawn: false,
      }];
    }
    return [{
      projectId: item.projectId,
      title: item.title,
      version: item.version,
      status: 'published' as const,
      parentVersion: published?.version ?? '',
      organization: current.organization.name,
      withdrawn: item.withdrawn,
    }];
  });
  const start = (page - 1) * pageSize;
  return ok({
    ancestors: ancestors(current, project.parentReleaseId).map((ancestor) => ({
      ...ancestor,
      projectId: ancestor.releaseId,
      status: ancestor.withdrawn ? 'withdrawn' as const : 'published' as const,
    })),
    children: {
      items: publishedChildren.slice(start, start + pageSize),
      page,
      pageSize,
      total: publishedChildren.length,
    },
    sourceDownload: project.parentReleaseId ? 'available' as const : 'none' as const,
  });
}

function listQuizzes(body: Body): DemoResult {
  const current = state();
  const project = current ? findProject(current, text(body.projectId)) : null;
  if (!current || !project) return fail(404, 'Project not found');
  const quizzes = [...project.quizzes].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).map(quizView);
  return ok(quizzes);
}

function draftFrom(value: unknown) {
  if (!value || typeof value !== 'object') return null;
  const draft = value as { description?: string | null; questions?: LabQuizQuestion[] };
  if (!Array.isArray(draft.questions)) return null;
  return { description: draft.description?.trim() || null, questions: draft.questions };
}

function createQuiz(body: Body): DemoResult {
  const current = state();
  const project = current ? findProject(current, text(body.projectId)) : null;
  if (!current || !project) return fail(404, 'Project not found');
  const title = text(body.title).trim();
  const draft = draftFrom(body.draft);
  if (!title || !draft) return fail(400, 'Add a title and at least one question');
  let view: LabQuizView | null = null;
  mutate((next) => {
    const target = findProject(next, project.id);
    if (!target) return;
    const at = nowIso();
    const quiz: DemoQuiz = {
      id: crypto.randomUUID(),
      projectId: target.id,
      title,
      status: 'draft',
      updatedAt: at,
      draft,
      currentVersionId: null,
      versions: [],
    };
    target.quizzes.unshift(quiz);
    target.updatedAt = at;
    view = quizView(quiz);
  });
  return ok(view);
}

function updateQuiz(body: Body): DemoResult {
  const current = state();
  const found = current ? findQuiz(current, text(body.quizId)) : null;
  if (!found) return fail(404, 'Quiz not found');
  if (found.quiz.status === 'open' && body.draft) return fail(400, 'Close the quiz before changing it');
  const title = text(body.title).trim();
  const draft = body.draft === undefined ? null : draftFrom(body.draft);
  if (body.draft !== undefined && !draft) return fail(400, 'Add at least one question');
  let view: LabQuizView | null = null;
  mutate((next) => {
    const target = findQuiz(next, found.quiz.id);
    if (!target) return;
    if (title) target.quiz.title = title;
    if (draft) target.quiz.draft = draft;
    target.quiz.updatedAt = nowIso();
    view = quizView(target.quiz);
  });
  return ok(view);
}

function openQuiz(body: Body): DemoResult {
  const current = state();
  const found = current ? findQuiz(current, text(body.quizId)) : null;
  if (!found) return fail(404, 'Quiz not found');
  if (found.quiz.status === 'open') return fail(409, 'This quiz is already open');
  if (found.quiz.draft.questions.length === 0) return fail(400, 'Add a question before opening the quiz');
  let view: LabQuizView | null = null;
  mutate((next) => {
    const target = findQuiz(next, found.quiz.id);
    if (!target) return;
    const at = nowIso();
    const version: DemoQuizVersion = {
      id: crypto.randomUUID(),
      version: (target.quiz.versions.at(-1)?.version ?? 0) + 1,
      description: target.quiz.draft.description,
      questions: structuredClone(target.quiz.draft.questions),
      createdAt: at,
      responses: [],
    };
    target.quiz.versions.push(version);
    target.quiz.currentVersionId = version.id;
    target.quiz.status = 'open';
    target.quiz.updatedAt = at;
    view = quizView(target.quiz);
  });
  return ok(view);
}

function closeQuiz(body: Body): DemoResult {
  const current = state();
  const found = current ? findQuiz(current, text(body.quizId)) : null;
  if (!found) return fail(404, 'Quiz not found');
  if (found.quiz.status !== 'open') return fail(400, 'This quiz is not open');
  let view: LabQuizView | null = null;
  mutate((next) => {
    const target = findQuiz(next, found.quiz.id);
    if (!target) return;
    target.quiz.status = 'closed';
    target.quiz.updatedAt = nowIso();
    view = quizView(target.quiz);
  });
  return ok(view);
}

function deleteQuiz(body: Body): DemoResult {
  const current = state();
  const found = current ? findQuiz(current, text(body.quizId)) : null;
  if (!found) return fail(404, 'Quiz not found');
  const total = found.quiz.versions.reduce((sum, version) => sum + version.responses.length, 0);
  if (total > 0) return fail(400, 'Quizzes with responses are kept');
  mutate((next) => {
    const project = findProject(next, found.project.id);
    if (!project) return;
    project.quizzes = project.quizzes.filter((quiz) => quiz.id !== found.quiz.id);
  });
  return ok({ success: true });
}

function quizResponses(body: Body): DemoResult {
  const current = state();
  const found = current ? findQuiz(current, text(body.quizId)) : null;
  if (!current || !found) return fail(404, 'Quiz not found');
  const role = roleOf(current, found.project.teamId);
  const canExport = role === 'owner' || role === 'manager';
  const page = pageNumber(body.page);
  const pageSize = pageNumber(body.pageSize, 20);
  const versionId = text(body.versionId);
  const version = versionId
    ? found.quiz.versions.find((item) => item.id === versionId) ?? null
    : found.quiz.versions.find((item) => item.id === found.quiz.currentVersionId) ?? null;
  if (!version) {
    return ok({ canExport, summary: null, responses: { items: [], page, pageSize, total: 0 } });
  }
  const responses = [...version.responses].sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
  const start = (page - 1) * pageSize;
  return ok({
    canExport,
    summary: quizSummary(version),
    responses: { items: responses.slice(start, start + pageSize), page, pageSize, total: responses.length },
  });
}

function exportQuiz(body: Body): DemoResult {
  const current = state();
  const found = current ? findQuiz(current, text(body.quizId)) : null;
  if (!current || !found) return fail(404, 'Quiz not found');
  const role = roleOf(current, found.project.teamId);
  if (role !== 'owner' && role !== 'manager') {
    return fail(403, 'Only team owners, managers, and organization administrators can download responses');
  }
  const versionId = text(body.versionId);
  const version = found.quiz.versions.find((item) => item.id === (versionId || found.quiz.currentVersionId));
  if (!version) return fail(400, 'This quiz has not been opened');
  const format = body.format === 'json' ? 'json' : 'csv';
  const filename = `${fileSlug(found.quiz.title)}-v${version.version}.${format}`;
  const responses = [...version.responses].sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
  if (format === 'json') {
    return ok({
      filename,
      contentType: 'application/json',
      body: JSON.stringify({
        title: found.quiz.title,
        version: version.version,
        responses: responses.map((response) => ({
          submittedAt: response.submittedAt,
          updatedAt: response.updatedAt,
          respondent: response.respondent,
          answers: response.answers.map((answer) => ({ prompt: answer.prompt, answer: answer.answer })),
        })),
      }, null, 2),
    });
  }
  const headers = ['Name', 'Email', 'Submitted', 'Updated', ...version.questions.map((question, index) => question.prompt || `Question ${index + 1}`)];
  const lines = responses.map((response) => [
    response.respondent.name,
    response.respondent.email,
    response.submittedAt,
    response.updatedAt,
    ...version.questions.map((question) => response.answers.find((answer) => answer.questionId === question.id)?.answer ?? ''),
  ].map(csvCell).join(','));
  return ok({
    filename,
    contentType: 'text/csv;charset=utf-8',
    body: [headers.map(csvCell).join(','), ...lines].join('\n'),
  });
}

function showcase(body: Body): DemoResult {
  const current = state();
  const project = current ? findProject(current, text(body.projectId)) : null;
  if (!current || !project) return fail(404, 'Project not found');
  return ok(showcaseState(current, project));
}

function showcaseState(draft: DemoState, project: DemoProject): LabShowcaseState {
  const role = roleOf(draft, project.teamId);
  const published = publishedRelease(project);
  const sourceReady = published?.sourcePackage?.status === 'passed';
  const blocked = !published
    ? 'Publish a version first'
    : sourceReady
      ? null
      : 'Add a source package that passed the security check';
  return {
    canManage: role === 'owner' || role === 'manager',
    releaseId: published?.id ?? null,
    sourceLocked: Boolean(project.parentReleaseId),
    blocked,
    softwareLicenses: SOFTWARE_LICENSES,
    assetLicenses: ASSET_LICENSES,
    submission: project.showcase.submission,
  };
}

function saveShowcase(body: Body): DemoResult {
  const current = state();
  if (!current) return fail(404, 'Release not found');
  const found = findRelease(current, text(body.releaseId));
  if (!found) return fail(404, 'Release not found');
  const role = roleOf(current, found.project.teamId);
  if (role !== 'owner' && role !== 'manager') {
    return fail(403, 'Only team owners and managers can submit to the showcase');
  }
  if (found.release.status !== 'published') return fail(400, 'Only a published version can be submitted');
  if (found.release.sourcePackage?.status !== 'passed') {
    return fail(400, 'Add a source package that passed the security check');
  }
  const software = text(body.openSourceLicense);
  const asset = text(body.assetLicense);
  if (!SOFTWARE_LICENSES.some((license) => license.id === software) || !ASSET_LICENSES.some((license) => license.id === asset)) {
    return fail(400, 'Choose a licence');
  }
  let view: LabShowcaseState | null = null;
  mutate((draft) => {
    const next = findRelease(draft, found.release.id);
    if (!next) return;
    const existing = next.project.showcase.submission;
    next.project.showcase.submission = {
      id: existing?.id ?? crypto.randomUUID(),
      status: 'draft',
      slug: existing?.slug ?? fileSlug(next.release.title),
      openSourceLicense: software,
      assetLicense: asset,
      assetDeclaration: asset === 'ownership' ? text(body.assetDeclaration).trim() || null : null,
      sourcePublished: next.project.parentReleaseId ? false : body.sourcePublished === true,
      reviewNote: null,
      submittedAt: null,
      reviewedAt: null,
    };
    view = showcaseState(draft, next.project);
  });
  return ok(view);
}

function withdrawShowcase(body: Body): DemoResult {
  const current = state();
  const found = current ? findRelease(current, text(body.releaseId)) : null;
  if (!found) return fail(404, 'Release not found');
  const status = found.project.showcase.submission?.status;
  if (!status || !['in_review', 'changes_requested', 'approved'].includes(status)) {
    return fail(400, 'This showcase submission cannot be withdrawn');
  }
  let view: LabShowcaseState | null = null;
  mutate((draft) => {
    const next = findRelease(draft, found.release.id);
    if (!next?.project.showcase.submission) return;
    next.project.showcase.submission = {
      ...next.project.showcase.submission,
      status: 'withdrawn',
      reviewNote: 'Withdrawn by the project team',
    };
    view = showcaseState(draft, next.project);
  });
  return ok(view);
}

function me(): DemoResult {
  const current = state();
  if (!current) return fail(401, 'Sign in to continue');
  return ok(current.user);
}

const handlers: Record<string, Handler> = {
  '/auth/me': me,
  '/auth/logout': () => ok({ success: true }),
  '/auth/onboarding-info': (body) => context(body).ok
    ? ok({
      firstName: state()?.user.firstName ?? '',
      lastName: state()?.user.lastName ?? '',
      orgName: state()?.organization.name ?? null,
      notice: null,
    })
    : fail(404, 'Labs is not available'),
  '/labs/context': context,
  '/labs/projects/list': listProjects,
  '/labs/projects/create': createProject,
  '/labs/projects/get': getProject,
  '/labs/projects/update-unit': updateUnit,
  '/labs/projects/update-drm': updateDrm,
  '/labs/projects/delete': deleteProject,
  '/labs/projects/start-release': startRelease,
  '/labs/teams/list': listTeams,
  '/labs/teams/create': createTeam,
  '/labs/teams/get': getTeam,
  '/labs/teams/update': updateTeam,
  '/labs/teams/members/add': addMember,
  '/labs/teams/members/remove': removeMember,
  '/labs/teams/members/set-role': setRole,
  '/labs/teams/ownership/claim': claimOwnership,
  '/labs/releases/update': updateRelease,
  '/labs/releases/narrow-visibility': narrowVisibility,
  '/labs/releases/stop-remixing': stopRemixing,
  '/labs/discover': discover,
  '/labs/remixes/offer': remixOffer,
  '/labs/remixes/create': createRemix,
  '/labs/remixes/lineage': lineage,
  '/labs/quizzes/list': listQuizzes,
  '/labs/quizzes/create': createQuiz,
  '/labs/quizzes/update': updateQuiz,
  '/labs/quizzes/open': openQuiz,
  '/labs/quizzes/close': closeQuiz,
  '/labs/quizzes/delete': deleteQuiz,
  '/labs/quizzes/responses': quizResponses,
  '/labs/quizzes/export': exportQuiz,
  '/labs/showcase/get': showcase,
  '/labs/showcase/save': saveShowcase,
  '/labs/showcase/withdraw': withdrawShowcase,
};

export function handleDemoRequest(url: string, body: unknown): DemoResult {
  const path = normalize(url);
  if (path === '/labs/remixes/source') return fail(400, SOURCE_MESSAGE);
  if (BLOCKED.has(path)) return fail(400, BLOCKED_MESSAGE);
  const handler = handlers[path];
  if (!handler) return fail(400, 'This action is not available in the demo.');
  return handler(asBody(body));
}
